import React, { useEffect, useState, useRef, useCallback } from "react";
import "./Sidebar.css";
import {
  voteNode,
  saveVoteResultAsNodes,
  addNodeToFirestore,
  updateParentChildrenIds,
  addComment,
} from "../../modules/firebaseAPI";
import { db } from "../../config/firebase";
import {
  doc,
  updateDoc,
  serverTimestamp,
  arrayUnion,
} from "firebase/firestore";

const ChatPageSidebar = ({
  className,
  voteTime,
  currentNode,
  nodes,
  setNodes,
  userId,
  onResetNodes,
  voteMethod,
  story,
  setStory,
}) => {
  const [newNodeText, setNewNodeText] = useState("");
  const [remainingTime, setRemainingTime] = useState(voteTime);
  const [timerStarted, setTimerStarted] = useState(false);
  const [chosenNode, setChosenNode] = useState(null);
  const [round, setRound] = useState(story?.currentRound || 1);
  const timerRef = useRef(null);

  // ラウンド開始時に固定親ノードIDをセット
  const [fixedParentId, setFixedParentId] = useState(
    story?.currentParentId || currentNode?.id || null
  );

  // 本文モーダル状態
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (story?.currentRound !== round) {
      // ★ currentNode ではなく、Firestore の currentParentId を絶対的に使う
      setFixedParentId(story.currentParentId || null);
    }
  }, [story?.currentRound, story?.currentParentId, round]);

  useEffect(() => {
    if (voteMethod === "author") return;
    setRemainingTime(voteTime * 60); // 秒に変換
  }, [voteTime, voteMethod]);

  useEffect(() => {
    if (!timerStarted) return;
    timerRef.current = setInterval(() => {
      setRemainingTime((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [timerStarted]);

  const chooseNode = useCallback((voteMethod, nodes, story) => {
    if (!nodes || nodes.length === 0) return null;
    switch (voteMethod) {
      case "author":
        return (
          nodes.find(
            (n) => Array.isArray(n.voters) && n.voters.includes(story.createdBy)
          ) || null
        );
      case "timed":
        return nodes[Math.floor(Math.random() * nodes.length)];
      case "vote":
        const maxVotes = Math.max(...nodes.map((n) => n.votes || 0));
        const topNodes = nodes.filter((n) => (n.votes || 0) === maxVotes);
        return topNodes[Math.floor(Math.random() * topNodes.length)];
      default:
        return null;
    }
  }, []);

  const adoptRoundNodes = useCallback(
    async (node) => {
      if (!story || !node) return;
      const currentRoundNodes = nodes.filter((n) => n.round === round);

      setChosenNode(node);

      await saveVoteResultAsNodes(
        story.id,
        currentNode?.id,
        currentRoundNodes,
        node,
        round
      );

      await updateDoc(doc(db, "stories", story.id), {
        currentNodeContent: (story.currentNodeContent || "") + " " + node.text,
        currentRound: round + 1,
        currentParentId: node.id,
        updatedAt: serverTimestamp(),
      });

      setStory((prev) => ({
        ...prev,
        currentNodeContent: (prev.currentNodeContent || "") + " " + node.text,
        currentRound: round + 1,
        currentParentId: node.id,
      }));

      try {
        await addComment({
          nodeId: currentNode?.id || "system",
          userId: "system",
          content: `「${node.text}」が採用されました 🎉`,
        });
        //console.log("採用アナウンスを投稿しました");
      } catch (err) {
        //console.error("採用アナウンス送信失敗:", err);
      }

      onResetNodes?.();
      setRound((prev) => prev + 1);
      setFixedParentId(node.id);

      // ★ ここで次ラウンドのカウントダウンを再スタート
      setRemainingTime(voteTime);
    },
    [nodes, round, story, currentNode, voteTime, onResetNodes, setStory]
  );

  useEffect(() => {
    if (!timerStarted || remainingTime > 0 || !story) return;

    setTimerStarted(false); // タイマーを止める（無限ループ防止）

    const currentRoundNodes = nodes.filter((n) => n.round === round);
    const node = chooseNode(voteMethod, currentRoundNodes, story);
    if (!node) return;

    adoptRoundNodes(node); // 次ラウンド開始は adopt の最後で行う
  }, [
    remainingTime,
    timerStarted,
    voteMethod,
    nodes,
    round,
    story,
    adoptRoundNodes,
    chooseNode,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newNodeText.trim()) return;

    const parentId = fixedParentId || (round === 1 ? story.firstNodeId : null);

    const newNodeObj = {
      text: newNodeText,
      votes: 0,
      voters: [],
      parentId,
      childrenIds: [],
      status: [],
      round,
      createdAt: new Date(),
    };

    try {
      const newDocRef = await addNodeToFirestore(story.id, newNodeObj);

      if (parentId) {
        await updateParentChildrenIds(
          story.id,
          parentId,
          arrayUnion(newDocRef.id)
        );
        setNodes((prev) =>
          prev.map((n) =>
            n.id === parentId
              ? { ...n, childrenIds: [...(n.childrenIds || []), newDocRef.id] }
              : n
          )
        );
      }

      setNodes((prev) => {
        if (prev.some((node) => node.id === newDocRef.id)) return prev;
        return [...prev, { id: newDocRef.id, ...newNodeObj }];
      });

      setNewNodeText("");
    } catch (err) {
      console.error("❌ ノード追加失敗:", err);
    }
  };

  const currentRoundNodes = nodes.filter((n) => n.round === round);

  return (
    <div className={`chat-sidebar ${className || ""}`}>
      {/* 本文モーダル部分 */}
      <div className="sidebar-section">
        <h3>ここまでの本文</h3>
        <p
          onDoubleClick={() => setIsModalOpen(true)}
          style={{ cursor: "pointer" }}
        >
          {story?.currentNodeContent
            ? story.currentNodeContent.split(" ").map((text, index) => (
                <React.Fragment key={index}>
                  {text}
                  <br />
                </React.Fragment>
              ))
            : "本文がありません"}
          {chosenNode?.text && (
            <>
              <br />
              <strong>最後に採用された選択肢:</strong> {chosenNode.text}
            </>
          )}
        </p>
      </div>

      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxHeight: "70%", overflowY: "auto" }}
          >
            <button
              className="modal-close-btn"
              onClick={() => setIsModalOpen(false)}
            >
              ×
            </button>
            <div>
              {story?.currentNodeContent
                ? story.currentNodeContent.split(" ").map((text, index) => (
                    <React.Fragment key={index}>
                      {text} <br />
                    </React.Fragment>
                  ))
                : "本文がありません"}
            </div>
          </div>
        </div>
      )}

      {/* 現在の投票方法 */}
      <div className="sidebar-section">
        <h3>現在の投票方法</h3>
        <p>
          {voteMethod === "author" && "スレ主が選択肢を選択します"}
          {voteMethod === "timed" &&
            "最初に選択肢がクリックされるとカウント開始し、ランダムに選択肢が採用されます"}
          {voteMethod === "vote" &&
            "最初に選択肢がクリックされるとカウント開始し、投票数が一番多い選択肢が採用されます"}
        </p>
      </div>

      {/* 残り時間 */}
      {voteMethod !== "author" && (
        <div className="sidebar-section">
          <h3>残り時間</h3>
          <p>
            {Math.floor(remainingTime / 60)}分 {remainingTime % 60}秒
          </p>
        </div>
      )}

      {/* 選択肢一覧 */}
      <div className="sidebar-section">
        <h3>次の選択肢一覧</h3>
        <ul>
          {currentRoundNodes.map((n) => {
            const totalVotes = currentRoundNodes.reduce(
              (sum, node) => sum + (node.votes || 0),
              0
            );
            const widthPercent = totalVotes
              ? ((n.votes || 0) / totalVotes) * 100
              : 0;

            const hasUserVotedInRound = currentRoundNodes.some((node) =>
              node.voters.includes(userId)
            );
            const canVote = !!story?.id && !hasUserVotedInRound;
            const isActiveStory = story?.id
              ? n.status.includes(story.id)
              : false;

            return (
              <li
                key={n.id}
                className={`choice-item ${isActiveStory ? "active-story" : ""}`}
              >
                <button
                  className={`vote-bar-button ${
                    canVote ? "active" : "disabled"
                  }`}
                  disabled={!canVote}
                  onClick={async () => {
                    if (!canVote) return;
                    try {
                      if (voteMethod === "author" && story.createdBy !== userId)
                        return;
                      await voteNode(story.id, n.id, userId);
                      currentRoundNodes.forEach((node) => {
                        if (!node.voters.includes(userId)) {
                          node.voters.push(userId);
                          node.votes =
                            (node.votes || 0) + (node.id === n.id ? 1 : 0);
                        }
                      });
                      if (
                        voteMethod === "author" &&
                        story.createdBy === userId
                      ) {
                        await adoptRoundNodes(n);
                        return;
                      }
                      if (!timerStarted) setTimerStarted(true);
                    } catch (err) {
                      console.error("投票失敗:", err);
                    }
                  }}
                >
                  <div
                    className="vote-bar-fill"
                    style={{ width: `${widthPercent}%` }}
                  />
                  <span className="choice-text">
                    {n.text}
                    {n.votes > 0 && ` (${n.votes}票)`}
                  </span>
                  {n.voters.includes(userId) && (
                    <span className="voted-label">投票済み</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {/* 選択肢追加 */}
      <div className="sidebar-section">
        <h3>選択肢を追加</h3>
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            value={newNodeText}
            onChange={(e) => setNewNodeText(e.target.value)}
            placeholder="新しい選択肢を入力"
          />
          <button type="submit">追加</button>
        </form>
      </div>
    </div>
  );
};

export default ChatPageSidebar;
